export default function ShiftLogs(
  Restangular,
  PersonaCollection,
  TagsService,
  Utils,
  toDateShortFilter,
  toTimeFilter,
  possessiveFilter,
  sdk,
  $sce,
  $sanitize,
  $injector
) {
  'ngInject';

  const { EVENT_STATUS } = sdk('constants');

  function _convertDate(dateString) {
    if (typeof dateString !== 'string') {
      return dateString;
    }

    let date = new Date(`${dateString.substring(0, 19)}+00:00`);
    const offset = date.getTimezoneOffset() * 60 * 1000;
    date = new Date(date.getTime() + offset);
    return date;
  }

  function _restangularizeUser(log) {
    if (log.data) {
      if (log.data.user) {
        if (log.data.user.from) {
          Restangular.restangularizeElement(null, log.data.user.from, 'users');
        }
        if (log.data.user.to) {
          Restangular.restangularizeElement(null, log.data.user.to, 'users');
        }
      }
    }

    Restangular.restangularizeElement(null, log.user, 'users');
  }

  function _restangularizeGroups(log) {
    ['position', 'location'].forEach((type) => {
      if (log.data[type]) {
        ['from', 'to'].forEach((prop) => {
          if (log.data[type][prop]) {
            if (log.data[type][prop].id in PersonaCollection.personasDict) {
              log.data[type][prop] = PersonaCollection.personasDict[log.data[type][prop].id];
            } else {
              log.data[type][prop].type = type;
              Restangular.restangularizeElement(null, log.data[type][prop], 'groups');
            }
          }
        });
      }
    });
  }

  function _convertDates(log) {
    if (log.data.dtstart) {
      log.data.dtstart.from = _convertDate(log.data.dtstart.from);
      log.data.dtstart.to = _convertDate(log.data.dtstart.to);
    }

    if (log.data.dtend) {
      log.data.dtend.from = _convertDate(log.data.dtend.from);
      log.data.dtend.to = _convertDate(log.data.dtend.to);
    }
  }

  function _handleLeaveChange(log, summary) {
    if (!summary?.from && summary?.to) {
      log.changes.push(
        `Added reason: <strong clamp-html="3" clamp-html-data="summary.to">${summary.to}</strong>`
      );
    }
    if (summary?.from && !summary?.to) {
      log.changes.push(
        `Deleted reason: <strong clamp-html="3" clamp-html-data="summary.from">${summary.from}</strong>`
      );
    }
    if (summary?.from && summary?.to) {
      log.changes.push(
        `Changed reason from <strong clamp-html="3" clamp-html-data="summary.from">${summary.from}</strong> to <strong>${summary.to}</strong>`
      );
    }
  }

  function _getChanges(log) {
    log.changes = [];
    const getChangesByType = {
      created: () => ['Created the shift'],
      created_and_published: () => ['Created the shift', 'Published the shift'],
      created_via_clockin: () => ['Created the shift by clocking in'],
      created_via_timesheet_edit: () => ['Created the shift by adding a timesheet'],
      created_via_import: () => {
        const name = log.data?.integration?.name;
        const platform = name
          ? `${name.charAt(0).toUpperCase()}${name.slice(1).toLowerCase()}`
          : 'a 3rd party platform';
        return [`Imported the shift from ${platform}`];
      },
      auto_assigned: () => ['Auto-assigned the shift'],
      copied_and_assigned: () => ['Copied and auto-assigned the shift'],
      published: () => ['Published the shift'],
      cancelled: () => ['Deleted the shift'],
      cancelled_via_location: () => ['Deleted the shift by archiving/deleting location'],
      cancelled_via_position: () => ['Deleted the shift by archiving/deleting position'],
      cancelled_via_user: () => ['Deleted the shift by deactivating/deleting assignee'],
      cancelled_via_user_delete: () => ['Deleted the shift by deleting assignee'],
      cancelled_via_user_deactivate: () => ['Deleted the shift by deactivating assignee'],
      made_available: () => ['Made the shift available'],
      availability_cancelled: () => ["Cancelled shift's availability"],
      received_application: () => ['Applied for the shift'],
      application_denied: () => {
        let whose = log.data.user.to.fullname;
        whose += /s$/i.test(whose) ? "'" : "'s";
        return [`<span class="red">Denied</span> ${whose} application for the shift`];
      },
      application_approved: () => {
        let whose = log.data.user.to.fullname;
        whose += /s$/i.test(whose) ? "'" : "'s";
        return [`<span class="green">Approved</span> ${whose} application for the shift`];
      },
      application_retracted: () => ['Retracted application'],
      made_swappable: () => [`Offered the shift to ${log.data.user.to.fullname}`],
      swap_approved: () => ['<span class="green">Approved</span> the shift offer'],
      swap_accepted: () => ['<span class="green">Accepted</span> the shift offer'],
      swap_rejected: () => ['<span class="red">Rejected</span> the shift offer'],
      swap_cancelled: () => ['Cancelled the shift offer'],
      leave_approved: () => ['Approved the time off request'],
      leave_requested: () => ['Requested the time off'],
      change_cancelled: () => ['Undone changes'],
      transaction_reverted: () => ['Redone changes'],
      leave_cancelled: () => ['Cancelled the time off request'],
      leave_denied: () => ['<span class="red">Denied the time off request</span>'],
      double_exchange_accepted: () => ['<span class="green">Accepted the shift swap offer</span>'],
      double_exchange_approved: () => ['<span class="green">Approved the shift swap offer</span>'],
      double_exchange_rejected: () => ['<span class="red">Rejected the shift swap offer</span>'],
      double_exchange_denied: () => ['<span class="red">Denied the shift swap offer</span>'],
      double_exchange_cancelled: () => ['<span class="red">Cancelled the shift swap offer</span>'],
      accepted: () => ['<span class="green">Accepted</span> the shift'],
    };

    if (log.type in getChangesByType) {
      log.changes = [...getChangesByType[log.type]()];
    } else if (log.type === 'leave_changed') {
      log.changes = ['Edited the time off request'];
      if (log.data) {
        const { dtstart, dtend, summary } = log.data;

        _handleTimeChanges(log, dtstart, dtend);
        _handleLeaveChange(log, summary);
      }
    } else if (log.type === 'double_exchange_created') {
      log.changes.push(_getSwapCreatedMessage(log));
    } else if (log.type === 'changed') {
      _getChangedLogChanges(log);
    } else if (log.type === 'rejected') {
      log.changes = ['<span class="red">Denied</span> the shift'];

      if (log.data.reason) {
        log.changes.push(`Comment: ${log.data.reason}`);
      }
    } else if (log.type == 'assignee_notes_updated') {
      _handleNotes(log, log.data);
    } else if (!__TEST__) {
      console.log(`unsupported history type: ${log.type}`);
    }

    return log.changes;
  }

  function _getSwapCreatedMessage(log) {
    const { offeree, offereeShift } = log.data;
    const name = PersonaCollection.personasDict[offeree.id].name;
    const time = `${toTimeFilter(offereeShift.dtstart)} - ${toTimeFilter(offereeShift.dtend)}`;

    return `Offered to swap the shift with ${possessiveFilter(name)} shift on ${toDateShortFilter(
      offereeShift.dtstart
    )}, ${time}`;
  }

  // eslint-disable-next-line complexity
  function _getChangedLogChanges(log) {
    const {
      user,
      dtstart,
      dtend,
      position,
      location,
      summary,
      breakDuration,
      available,
      availableSlots,
      slots,
      status,
      tags,
      taskTemplates,
    } = log.data;

    if (status?.from === EVENT_STATUS.PUBLISHED && status?.to === EVENT_STATUS.PLANNING) {
      log.changes.push('Unpublished the shift');
    }

    if (user && !user.from && user.to) {
      log.changes.push(`Assigned <span class="green">${user.to.fullname}</span>`);
    }
    if (user?.from && !user.to) {
      log.changes.push(`Unassigned <span class="red">${user.from.fullname}</span>`);
    }
    if (user?.from && user.to) {
      log.changes.push(
        `Reassigned the shift<br />From <span class="red">${user.from.fullname}</span> to <span class="green">${user.to.fullname}</span>`
      );
    }
    if (breakDuration) {
      log.changes.push(
        `Break duration from <span class="red">${breakDuration.from}</span> to <span class="green">${breakDuration.to}</span>`
      );
    }
    if (available) {
      log.changes.push(
        available.to ? 'Made the shift available' : "Cancelled shift's availability"
      );
    }
    if (availableSlots) {
      if (availableSlots.to !== null && availableSlots.from === null) {
        log.changes.push(
          `Set ${availableSlots.to} available shift slot${availableSlots.to !== 1 ? 's' : ''}`
        );
      } else if (availableSlots.to === null && availableSlots.from !== null) {
        log.changes.push(
          `Removed ${availableSlots.from} available shift slot${availableSlots.to !== 1 ? 's' : ''}`
        );
      } else if (availableSlots.to !== null && availableSlots.from !== null) {
        log.changes.push(
          `Changed available shift slots from ${availableSlots.from} to ${availableSlots.to}`
        );
      }
    }

    _handleGroupChanges(log, location, position);
    _handleTimeChanges(log, dtstart, dtend);
    _handleNotes(log, summary);
    _handleTags(log, tags);
    _handleTasks(log, taskTemplates);
    _handleSlots(log, slots);
  }

  function _handleTags(log, tags) {
    if (!tags) {
      return;
    }

    const fromIdsSet = new Set(tags.from.map((t) => t.id));
    const toIdsSet = new Set(tags.to.map((t) => t.id));

    const knownTagsDict = TagsService.tags.reduce((agg, tag) => ((agg[tag.id] = tag), agg), {});

    const deletedIds = Utils.setToArray(fromIdsSet).filter((id) => !toIdsSet.has(id));
    const addedIds = Utils.setToArray(toIdsSet).filter((id) => !fromIdsSet.has(id));

    const tagName = (tagId) =>
      knownTagsDict[tagId]?.name ? `${knownTagsDict[tagId]?.name}` : '[unknown]';
    const deletedJoinedStr = Utils.addJoin(deletedIds.map((tagId) => tagName(tagId)));
    const addedJoinedStr = Utils.addJoin(addedIds.map((tagId) => tagName(tagId)));

    if (deletedIds.length && addedIds.length) {
      log.changes.push(
        `Edited the tag${deletedIds.length > 1 || addedIds.length > 1 ? 's' : ''}</span>`
      );
      log.changes.push(
        `From <span class="red">${deletedJoinedStr}</span> to <span class="green">${addedJoinedStr}</span>`
      );
    } else if (deletedIds.length) {
      log.changes.push(`Removed the tag${deletedIds.length !== 1 ? 's' : ''}`);
      log.changes.push(`<span class="red">${deletedJoinedStr}</span>`);
    } else if (addedIds.length) {
      log.changes.push(`Added the tag${addedIds.length !== 1 ? 's' : ''}`);
      log.changes.push(`<span class="green">${addedJoinedStr}</span>`);
    }
  }

  function _handleTasks(log, tasksTemplates) {
    if (!tasksTemplates) {
      return;
    }

    // for TaskTemplatesService to be inited it needs the Authentication.user to be loaded
    // it's not a problem for controllers but in a service such as this one it would blow up
    // hence we need to load it on demand via injector
    const TaskTemplatesService = $injector.get('TaskTemplatesService');

    const fromIdsSet = new Set(tasksTemplates.from.map((t) => t.id));
    const toIdsSet = new Set(tasksTemplates.to.map((t) => t.id));

    const knownTaskTemplatesDict = TaskTemplatesService.allTemplates.reduce(
      (agg, tag) => ((agg[tag.id] = tag), agg),
      {}
    );

    const deletedIds = Utils.setToArray(fromIdsSet).filter((id) => !toIdsSet.has(id));
    const addedIds = Utils.setToArray(toIdsSet).filter((id) => !fromIdsSet.has(id));

    const taskTemplateName = (taskTemplateId) =>
      knownTaskTemplatesDict[taskTemplateId]?.title
        ? `${knownTaskTemplatesDict[taskTemplateId]?.title}`
        : '[unknown]';
    const deletedJoinedStr = Utils.addJoin(
      deletedIds.map((taskTemplateId) => taskTemplateName(taskTemplateId))
    );
    const addedJoinedStr = Utils.addJoin(
      addedIds.map((taskTemplateId) => taskTemplateName(taskTemplateId))
    );

    if (deletedIds.length && addedIds.length) {
      log.changes.push(
        `Edited the task${deletedIds.length > 1 || addedIds.length > 1 ? 's' : ''}</span>`
      );
      log.changes.push(
        `From <span class="red">${deletedJoinedStr}</span> to <span class="green">${addedJoinedStr}</span>`
      );
    } else if (deletedIds.length) {
      log.changes.push(`Removed the task${deletedIds.length !== 1 ? 's' : ''}`);
      log.changes.push(`<span class="red">${deletedJoinedStr}</span>`);
    } else if (addedIds.length) {
      log.changes.push(`Added the task${addedIds.length !== 1 ? 's' : ''}`);
      log.changes.push(`<span class="green">${addedJoinedStr}</span>`);
    }
  }

  function _handleSlots(log, slots) {
    if (!slots?.from && slots?.to) {
      log.changes.push(`Added shift slots: <span class="green">${slots.to}</span>`);
    }
    if (slots?.from && !slots.to) {
      log.changes.push(`Deleted shift slots: <span class="red">${slots.from}</span>`);
    }
    if (slots?.from && slots.to) {
      log.changes.push(
        `Changed shift slots from <span class="red">${slots.from}</span> to <span class="green">${slots.to}</span>`
      );
    }
  }

  function _handleNotes(log, summary) {
    if (!summary?.from && summary?.to) {
      log.changes.push('Added notes');
      log.changes.push(`<span clamp-html="3" clamp-html-data="summary.to">${summary.to}</span>`);
    }
    if (summary?.from && !summary.to) {
      log.changes.push('Deleted notes');
      log.changes.push(
        `<span clamp-html="3" clamp-html-data="summary.from">${summary.from}</span>`
      );
    }
    if (summary?.from && summary.to) {
      log.changes.push('Edited notes');
      log.changes.push(
        `From <span class="red" clamp-html="3" clamp-html-data="summary.from">${summary.from}</span> to <span class="green" clamp-html="3" clamp-html-data="summary.from">${summary.to}</span>`
      );
    }
  }

  function _handleGroupChanges(log, location, position) {
    if (location) {
      log.changes.push('Edited the location');
      log.changes.push(
        `From <span class="red">${location.from.name}</span> to <span class="green">${location.to.name}</span>`
      );
    }
    if (position) {
      log.changes.push('Edited the position');
      log.changes.push(
        `From <span class="red">${position.from.name}</span> to <span class="green">${position.to.name}</span>`
      );
    }
  }

  function _handleTimeChanges(log, dtstart, dtend) {
    if (dtstart) {
      log.changes.push('Edited the start time');
      const sameDay = toDateShortFilter(dtstart.from) === toDateShortFilter(dtstart.to);
      const dateFromString = sameDay ? '' : `${toDateShortFilter(dtstart.from)} `;
      const dateToString = sameDay ? '' : `${toDateShortFilter(dtstart.to)} `;
      log.changes.push(
        `From <span class="red">${dateFromString}${toTimeFilter(
          dtstart.from
        )}</span> to <span class="green">${dateToString}${toTimeFilter(dtstart.to)}</span>`
      );
    }
    if (dtend) {
      log.changes.push('Edited the end time');
      const sameDay = toDateShortFilter(dtend.from) === toDateShortFilter(dtend.to);
      const dateFromString = sameDay ? '' : `${toDateShortFilter(dtend.from)} `;
      const dateToString = sameDay ? '' : `${toDateShortFilter(dtend.to)} `;
      log.changes.push(
        `From <span class="red">${dateFromString}${toTimeFilter(
          dtend.from
        )}</span> to <span class="green">${dateToString}${toTimeFilter(dtend.to)}</span>`
      );
    }
  }

  Restangular.extendModel('logs', (log) => {
    _restangularizeUser(log);

    if (log.data) {
      _restangularizeGroups(log);
      _convertDates(log);
    }

    log.posted = new Date(log.posted);
    log.changes = _getChanges(log);
    log.changes = log.changes.map((change) => $sce.trustAsHtml($sanitize(change)));

    return log;
  });

  return Restangular.service('logs');
}
